import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useGetSingleJob } from "../hooks/useGetSingleJob";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { formatJobYearsLabel } from "../utils/formatJobYearsLabel";

dayjs.extend(relativeTime);

export const ViewJobDetails = () => {
  const { id } = useParams();
  const { token } = useGetUserInfo();
  const { data, isLoading } = useGetSingleJob({
    id: id as unknown as number,
    token,
  });

  if (!data) return null;
  const postedDate = data.created_at
    ? dayjs(data.created_at).fromNow()
    : "Unknown";
  
  return (
    <div className="max-w-6xl mx-auto bg-white border rounded-2xl p-8">
      {/* Header */}
      <div className="flex justify-between items-start border-b pb-6">
        <div>
          <h1 className="text-2xl font-semibold">{data.title}</h1>

          <div className="flex items-center gap-4 mt-2 text-gray-600">
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line" />
              {data.location_city}
              {data.location_country && `, ${data.location_country}`}
            </span>

            <span className="flex items-center gap-1">
              <i className="ri-briefcase-line" />
              {data.employment_type.replace("-", " ")}
            </span>

            {data.created_at && (
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {postedDate}
              </span>
            )}
          </div>
        </div>

        <button className="bg-[#00094A] text-white px-6 py-2 rounded-lg">
          Apply Now
        </button>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-4 gap-6 mt-6 text-sm">
        <Info
          label="Location"
          value={`${data.location_city}${
            data.location_country ? `, ${data.location_country}` : ""
          }`}
        />

        <Info
          label="Employment Type"
          value={data.employment_type.replace("-", " ")}
        />

        <Info label="Job Level" value={data.level.replace("_", " ")} />

        {data.created_at && dayjs(data.created_at).isValid() && (
          <Info label="Posted" value={postedDate} />
        )}

        <Info
          label="Years of experience"
          value={formatJobYearsLabel(data.years_of_experience) || "—"}
        />

        <Info label="Job Type" value={data.type} />

        <Info
          label="Job Status"
          value={data.status !== "draft" ? "Open" : "Closed"}
        />
        <Info
          label="Application Deadline"
          value={dayjs(data.expiry_date).format("MMMM D, YYYY")}
        />
      </div>

      {/* About Company */}
      <Section title="About the Company">
        <p className="text-gray-600">{data.company_name}</p>
      </Section>

      {/* Job Description */}
      <Section title="Job Description">
        <p className="text-gray-600">{data.description}</p>
      </Section>

      {data.jobsummary && (
        <Section title="Job Summary">
          <p className="text-gray-600">{data.jobsummary}</p>
        </Section>
      )}

      {/* Skills */}
      {data.skills?.length > 0 && (
        <Section title="Skills">
          <div className="flex flex-wrap gap-3">
            {data.skills.map((skill) => (
              <span
                key={skill}
                className="bg-gray-100 px-4 py-2 rounded-lg text-sm"
              >
                {skill}
              </span>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

const Info = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-gray-400">{label}</p>
    <p className="font-medium text-gray-900 mt-1">{value}</p>
  </div>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="mt-10 border-t pt-6">
    <h2 className="font-semibold text-lg mb-3">{title}</h2>
    {children}
  </div>
);
